import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Card, Row, Col, Statistic } from "antd";
import { TrophyOutlined, DollarOutlined, FireOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { getGridTableHeight } from '../Common/util';

function Priority({ planList, spinflag }) {
    const [dataSource, setDataSource] = useState([]);
    const [statistics, setStatistics] = useState({
        total: 0,
        highPriority: 0,
        highCost: 0,
        veryDifficult: 0
    });

    useEffect(() => {
        if (planList && planList.length > 0) {
            // priority_score 기준으로 정렬 (높은 순)
            const sortedData = [...planList].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
            setDataSource(sortedData);

            // 통계 계산
            const stats = {
                total: planList.length,
                highPriority: planList.filter(item => item.priority_score && item.priority_score >= 3).length,
                highCost: planList.filter(item => {
                    // 💰💰💰 고비용 (cost_measure >= 4)
                    return item.cost_level_display && item.cost_level_display === '💰💰💰 고비용';
                }).length,
                veryDifficult: planList.filter(item => {
                    // 🔴 매우어려움 (difficulty_measure >= 4)
                    return item.difficulty_level_display && item.difficulty_level_display === '🔴 매우어려움';
                }).length
            };
            setStatistics(stats);
        }
    }, [planList]);

    const columns = [
        {
            title: '순위',
            key: 'rank',
            width: '60px',
            align: 'center',
            fixed: 'left',
            render: (_, __, index) => {
                const rank = index + 1;
                if (rank <= 3) {
                    return <Tag color="red" style={{ fontSize: '14px', fontWeight: 'bold' }}>{rank}</Tag>;
                } else if (rank <= 10) {
                    return <Tag color="orange">{rank}</Tag>;
                } else {
                    return <Tag>{rank}</Tag>;
                }
            }
        },
        {
            title: '우선순위 점수',
            dataIndex: 'priority_score',
            key: 'priority_score',
            width: '120px',
            align: 'center',
            sorter: (a, b) => (a.priority_score || 0) - (b.priority_score || 0),
            render: (value) => {
                if (!value) return '-';
                const score = parseFloat(value).toFixed(2);
                let color = '#52c41a'; // green
                if (value >= 3) color = '#f5222d'; // red
                else if (value >= 2) color = '#fa8c16'; // orange
                
                return <span style={{ color, fontWeight: 'bold', fontSize: '16px' }}>{score}</span>;
            }
        },
        {
            title: '자산정보',
            key: 'asset_info',
            width: '200px',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 'bold' }}>{record.asset_cd}</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>{record.asset_nm}</span>
                </Space>
            )
        },
        {
            title: '위험코드',
            dataIndex: 'risk_cd',
            key: 'risk_cd',
            width: '100px',
            align: 'center',
        },
        {
            title: '위험도',
            dataIndex: 'risk_score',
            key: 'risk_score',
            width: '80px',
            align: 'center',
            sorter: (a, b) => (a.risk_score || 0) - (b.risk_score || 0),
            render: (value) => {
                if (!value) return '-';
                let color = '#52c41a';
                if (value >= 60) color = '#f5222d';
                else if (value >= 30) color = '#fa8c16';
                
                return <Tag color={color}>{value}</Tag>;
            }
        },
        {
            title: '취약점',
            dataIndex: 'measure_nm',
            key: 'measure_nm',
            width: '250px',
            ellipsis: true,
        },
        {
            title: '비용수준',
            dataIndex: 'cost_level_display',
            key: 'cost_level_display',
            width: '150px',
            align: 'center',
            render: (value) => {
                if (!value) return '-';
                return <div style={{ fontSize: '14px' }}>{value}</div>;
            }
        },
        {
            title: '난이도',
            dataIndex: 'difficulty_level_display',
            key: 'difficulty_level_display',
            width: '150px',
            align: 'center',
            render: (value) => {
                if (!value) return '-';
                return <div style={{ fontSize: '14px' }}>{value}</div>;
            }
        },
        {
            title: '대응전략',
            dataIndex: 'apply_action',
            key: 'apply_action',
            width: '100px',
            align: 'center',
            render: (value) => {
                if (!value) return '-';
                const colorMap = {
                    '위험감소': 'green',
                    '위험수용': 'orange',
                    '위험이전': 'blue',
                    '위험회피': 'red'
                };
                return <Tag color={colorMap[value] || 'default'}>{value}</Tag>;
            }
        },
        {
            title: '조치일정',
            dataIndex: 'action_time',
            key: 'action_time',
            width: '100px',
            align: 'center',
        },
        {
            title: '담당자',
            dataIndex: 'action_manager',
            key: 'action_manager',
            width: '100px',
            align: 'center',
        }
    ];

    return (
        <>
            <div style={{ padding: '10px' }}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="전체 위험항목"
                                value={statistics.total}
                                prefix={<FireOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="고위험 항목 (우선순위 3.0 이상)"
                                value={statistics.highPriority}
                                valueStyle={{ color: '#cf1322' }}
                                prefix={<TrophyOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="고비용 항목"
                                value={statistics.highCost}
                                valueStyle={{ color: '#fa8c16' }}
                                prefix={<DollarOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="매우 어려움 항목"
                                value={statistics.veryDifficult}
                                valueStyle={{ color: '#722ed1' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
            <div style={{ padding: '0px 10px 10px 10px' }}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="id"
                    loading={spinflag}
                    scroll={{ x: 1500, y: getGridTableHeight(415) }}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total) => `총 ${total} 건`,
                    }}
                    bordered
                    size="small"
                    rowClassName={(record) => {
                        if (record.priority_score >= 3) return 'high-priority-row';
                        return '';
                    }}
                />
            </div>
            {/* <style jsx>{`
                .high-priority-row {
                    background-color: #fff1f0;
                }
                .high-priority-row:hover > td {
                    background-color: #00cc00 !important;
                }
            `}</style> */}
        </>
    );
}

export default Priority;